import React, { useState, useMemo } from 'react';
import { Box, Typography } from '@material-ui/core';
import { Progress } from '@backstage/core-components';
import SelectableFilter from './components/SidebarFilters';
import { useFilterQuantities, useProducts, usePagination } from './hooks';
import LoadMoreCard from './components/LoadMoreCard';
import TuneIcon from '@material-ui/icons/Tune';
import {
  Container,
  MainContent,
  EmptyState,
  EmptyStateTitle,
  CardsWrapper,
  ShowFiltersButton,
} from './CustomApiExplorer.styles';
import { useNavigate } from 'react-router-dom';
import ApiCard from './components/ApiCard';

const ITEMS_PER_PAGE = 8;

const CustomApiExplorer = () => {
  const { data: productsData, isLoading } = useProducts();
  const navigate = useNavigate();
  const [filters, setFilters] = useState<string[]>([]);
  const [isFiltersOpen, setIsFiltersOpen] = useState(true);
  const filteredData = useMemo(() => {
    if (!productsData?.elements) return [];

    return productsData.elements
      .filter(item => item.type === 'api')
      .filter(item => filters.length === 0 || filters.includes(item.tag));
  }, [filters, productsData?.elements]);

  const { visibleItems, showLoadMore, handleLoadMore } = usePagination(
    filteredData,
    ITEMS_PER_PAGE,
    filters,
  );

  const { quantities } = useFilterQuantities();

  const apiTypesOptions = useMemo(() => {
    if (!productsData?.elements) return [];

    const uniqueTags = [...new Set(productsData.elements.map(p => p.tag))];

    return uniqueTags
      .filter(tag => tag !== 'Documentation')
      .map(tag => ({
        id: tag,
        label: tag,
        qty: quantities[tag] || 0,
      }));
  }, [productsData?.elements, quantities]);

  if (isLoading) {
    return <Progress />;
  }

  return (
    <Container>
      {isFiltersOpen && (
        <SelectableFilter
          options={apiTypesOptions}
          selectedOptions={filters}
          onSelectionChange={setFilters}
          onClose={() => setIsFiltersOpen(false)}
        />
      )}

      <MainContent>
        {!isFiltersOpen && (
          <Box>
            <ShowFiltersButton
              startIcon={<TuneIcon />}
              onClick={() => setIsFiltersOpen(true)}
            >
              Show Filters
            </ShowFiltersButton>
          </Box>
        )}
        {productsData?.elements.length === 0 ? (
          <EmptyState>
            <EmptyStateTitle variant="h5">No APIs found</EmptyStateTitle>
            <Typography variant="body1">
              No APIs are registered in the catalog yet.
            </Typography>
          </EmptyState>
        ) : (
          <CardsWrapper>
            {visibleItems.map(product => (
              <ApiCard
                key={product.id}
                {...product}
                productType={product.tag}
                subtitle={product.specs[0]?.version}
                image={product.thumbnails[0]?.url}
                isNew={product.new}
                isDeprecated={product.deprecated}
                onActionClick={() => navigate(`/api-docs/${product.id}`)}
              />
            ))}
            {showLoadMore && <LoadMoreCard onClick={handleLoadMore} />}
          </CardsWrapper>
        )}
      </MainContent>
    </Container>
  );
};

export default CustomApiExplorer;
